import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { FileText, Plus } from "lucide-react";
import { AddBookingNoteModal } from "./AddBookingNoteModal";

type NoteSource = "reservation" | "guest";

interface NoteStringCardProps {
  content: string;
  index: number;
  source: NoteSource;
}

function NoteStringCard({ content, index, source }: NoteStringCardProps) {
  const { t } = useTranslation();
  const sourceLabel =
    source === "guest"
      ? t("reservation.notesSection.guestNotes")
      : t("reservation.notesSection.reservationNote");

  return (
    <div className="flex gap-3">
      <div className="text-foreground flex size-8 shrink-0 items-center justify-center rounded-full bg-neutral-200 text-xs font-semibold">
        {String(index + 1)}
      </div>
      <div className="flex flex-1 flex-col gap-1">
        <div className="text-foreground rounded-xl bg-amber-50 px-4 py-3 text-sm">
          {content}
        </div>
        <div className="text-muted-foreground flex flex-wrap items-center gap-1.5 px-1 text-xs">
          <FileText className="size-3 shrink-0" />
          <span>{sourceLabel}</span>
        </div>
      </div>
    </div>
  );
}

interface NotesSectionProps {
  bookingId?: string;
  notes?: string[];
  guestNotes?: string[];
}

export const NotesSection: React.FC<NotesSectionProps> = ({
  bookingId,
  notes,
  guestNotes,
}) => {
  const { t } = useTranslation();
  const [addOpen, setAddOpen] = useState(false);
  const list = [
    ...(notes ?? []).map((content) => ({
      content,
      source: "reservation" as const,
    })),
    ...(guestNotes ?? []).map((content) => ({
      content,
      source: "guest" as const,
    })),
  ];

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center justify-between">
        <h3 className="text-violet text-sm font-semibold">
          {t("reservation.notesSection.title")}
        </h3>
        {bookingId ? (
          <button
            type="button"
            onClick={() => setAddOpen(true)}
            className="text-foreground flex items-center gap-1 rounded-lg border border-neutral-200 bg-white px-3 py-1.5 text-sm font-medium shadow-sm transition-colors hover:bg-neutral-50"
          >
            {t("reservation.notesSection.addNote")}
            <Plus className="size-4" />
          </button>
        ) : null}
      </div>

      {list.length === 0 ? (
        <p className="text-muted-foreground text-xs">
          {t("reservation.notesSection.empty")}
        </p>
      ) : (
        <div className="flex flex-col gap-4">
          {list.map(({ content, source }, index) => (
            <NoteStringCard
              key={`${source}-${index}-${content.slice(0, 32)}`}
              content={content}
              index={index}
              source={source}
            />
          ))}
        </div>
      )}

      {bookingId ? (
        <AddBookingNoteModal
          bookingId={bookingId}
          open={addOpen}
          onOpenChange={setAddOpen}
        />
      ) : null}
    </div>
  );
};
